// ✅ Phase 6 (v6.13.73): Self-Improvement Node — Luna Self-Improvement Loop
//
// Conversation gap detection → suggestion staging → OWNER-CONFIRMED knowledge enrichment.
// This node makes the loop VISIBLE and CONFIGURABLE in the flow canvas (per the platform's
// node-driven architecture): toggles wire directly to `tenetSelfImproveService` behavior.
//
//  - isEnabled     → the whole loop on/off for this tenant (scan + digests + tool)
//  - autoScan      → the 6-hourly background conversation scan (manual scans always work)
//  - notifyOwner   → RULE-20 style WhatsApp digest when new gaps are found
//  - minConfidence → which suggestion quality levels are kept (high / medium / all)
//
// The LLM never writes knowledge: suggestions are staged through the 2-step confirmation
// protocol and land in the Knowledge node only after the owner confirms.
import React, { memo, useState, useMemo, useRef } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import { Brain, Sparkles, Lock, CheckCircle2, Bell, Search, ShieldCheck, TrendingUp } from 'lucide-react';

export interface SelfImproveNodeData {
  isEnabled?: boolean;
  autoScan?: boolean;
  notifyOwner?: boolean;
  minConfidence?: 'high' | 'medium' | 'all';
  isCustomizable?: boolean;
  [key: string]: unknown;
}

export const SelfImproveNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as SelfImproveNodeData;

  const isEnabled = nodeData.isEnabled !== false;
  const autoScan = nodeData.autoScan !== false;
  const notifyOwner = nodeData.notifyOwner !== false;
  const minConfidence = (nodeData.minConfidence as string) || 'medium';

  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  // ═══ PULSE INTEGRATION — EXACT ORDER (do not reorder) ═══
  const isCustomizable = nodeData.isCustomizable !== false;
  const { isTenantView, authToken, tenantApiKey, pulseState } = useTenantView();

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isLocked = isTenantView && !isCustomizable;
  // ═══ END PULSE INTEGRATION ═══

  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  const persistNodeDataDirectly = (allUpdates: Partial<SelfImproveNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          isEnabled,
          autoScan,
          notifyOwner,
          minConfidence,
          ...allUpdates,
        };
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'SELF_IMPROVE_NODE',
            data: payloadData,
            widgetData: payloadData,
          }),
        });
        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.error('[SelfImproveNode] Persist error:', err);
      }
    }, 400);
  };

  const handleFieldChange = (field: keyof SelfImproveNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly({ [field]: value } as Partial<SelfImproveNodeData>);
  };

  const ToggleRow = ({ icon, label, desc, value, field }: { icon: React.ReactNode; label: string; desc: string; value: boolean; field: keyof SelfImproveNodeData }) => (
    <button
      type="button"
      disabled={isLocked}
      onClick={() => handleFieldChange(field, !value)}
      className={`w-full flex items-start gap-2.5 rounded-xl px-3 py-2.5 border transition-all text-left ${
        value ? 'bg-sky-500/15 border-sky-400/40' : 'bg-slate-900/50 border-white/[0.06]'
      } ${isLocked ? 'opacity-60 cursor-not-allowed' : 'hover:border-sky-400/50'}`}
    >
      <div className={`mt-0.5 ${value ? 'text-sky-300' : 'text-slate-500'}`}>{icon}</div>
      <div className="flex-1 min-w-0">
        <div className={`text-[12px] font-bold ${value ? 'text-sky-200' : 'text-slate-300'}`}>{label}</div>
        <div className="text-[10px] text-slate-400 leading-snug mt-0.5">{desc}</div>
      </div>
      <div className={`w-9 h-5 rounded-full transition-all relative shrink-0 mt-0.5 ${value ? 'bg-sky-400' : 'bg-slate-700'}`}>
        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${value ? 'left-[18px]' : 'left-0.5'}`} />
      </div>
    </button>
  );

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-80 select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#06121F]/98 via-[#081426]/98 to-[#050D18]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-sky-400 ring-2 ring-sky-400/50 shadow-[0_0_35px_rgba(56,189,248,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-sky-500/40 hover:border-sky-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(56,189,248,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* Target (input) handle — LEFT side */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-self-improve"
        className="!bg-sky-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 z-20 shadow-[0_0_8px_rgba(56,189,248,0.8)]"
        title="Self-improvement loop input"
      />

      {/* ── Header ── */}
      <div className="flex items-center gap-2.5 px-4 pt-4 pb-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center shadow-[0_0_18px_rgba(56,189,248,0.55)]">
          <Brain size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-extrabold text-white tracking-tight">Luna Self-Improvement</div>
          <div className="text-[10px] text-sky-300/80 font-medium">🧠 GAP DETECTION → OWNER-CONFIRMED LEARNING</div>
        </div>
        <div className={`px-2 py-1 rounded-lg text-[9px] font-black border ${isEnabled ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40' : 'bg-slate-700/40 text-slate-400 border-slate-600/40'}`}>
          {isEnabled ? '● ACTIVE' : '○ OFF'}
        </div>
      </div>

      {isLocked && (
        <div className="mx-4 mb-2 flex items-center gap-1.5 text-[9px] text-amber-400 font-bold bg-amber-500/10 border border-amber-500/30 rounded-lg px-2 py-1">
          <Lock size={10} /> Locked by Admin
        </div>
      )}

      {/* ── Body ── */}
      <div className="px-4 pb-4 space-y-2">
        <div className="flex items-start gap-2 text-[10px] text-slate-400 bg-sky-500/[0.07] border border-sky-500/10 rounded-xl px-3 py-2">
          <ShieldCheck size={13} className="text-sky-400 mt-0.5 shrink-0" />
          <span>Luna customer ලාගේ conversations scan කරලා උත්තර දෙන්න බැරි වුණ ප්‍රශ්න හඳුනාගන්නවා. යෝජනා ඔයා confirm කරොත් විතරයි Knowledge Base එකට යන්නේ.</span>
        </div>

        <ToggleRow
          icon={<Sparkles size={14} />}
          label="Enable Self-Improvement"
          desc="ගුණාත්මක අඩුතැන් හඳුනාගෙන ඔයාගේ අනුමැතියෙන් Luna ඉගෙන ගන්නවා"
          value={isEnabled}
          field="isEnabled"
        />
        <ToggleRow
          icon={<Search size={14} />}
          label="Auto-Scan (every 6 hours)"
          desc="background එකේදි conversations automatically scan කරනවා"
          value={autoScan}
          field="autoScan"
        />
        <ToggleRow
          icon={<Bell size={14} />}
          label="Owner WhatsApp Digest"
          desc="අලුත් gaps හමු වුණොත් ඔයාගේ WhatsApp එකට summary එකක් එනවා"
          value={notifyOwner}
          field="notifyOwner"
        />

        {/* Confidence filter */}
        <div className="rounded-xl bg-slate-900/50 border border-white/[0.06] px-3 py-2.5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={13} className="text-sky-400" />
            <div className="text-[11px] font-bold text-slate-200">Minimum Confidence</div>
          </div>
          <div className="flex gap-1.5">
            {([
              { v: 'high', label: 'High' },
              { v: 'medium', label: 'Medium' },
              { v: 'all', label: 'All' },
            ] as const).map((o) => (
              <button
                key={o.v}
                type="button"
                disabled={isLocked}
                onClick={() => handleFieldChange('minConfidence', o.v)}
                className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${
                  minConfidence === o.v
                    ? 'bg-sky-400/20 border-sky-400/50 text-sky-200'
                    : 'bg-slate-900/40 border-white/[0.06] text-slate-400 hover:border-sky-400/30'
                } ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="text-[9px] text-slate-500 mt-1.5">Low-confidence යෝජනා මග හරින්න High තෝරන්න</div>
        </div>

        <div className="flex items-center gap-1.5 text-[9.5px] text-emerald-300/90 bg-emerald-500/[0.07] border border-emerald-500/15 rounded-lg px-2.5 py-1.5">
          <CheckCircle2 size={11} className="shrink-0" />
          <span>2-Step confirmation එකෙන් පමණයි knowledge writes — ඔයාගේ අනුමැතියකින් තොරව මොකුත් වෙනස් වෙන්නේ නෑ</span>
        </div>

        {saveIndicator && (
          <div className="text-[9px] text-emerald-300 font-bold text-center">{saveIndicator}</div>
        )}
      </div>

      {/* Source (output) handle — RIGHT side */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-self-improve"
        className="!bg-sky-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 z-20 shadow-[0_0_8px_rgba(56,189,248,0.8)]"
        title="Knowledge enrichment output"
      />
    </div>
  );
});

SelfImproveNode.displayName = 'SelfImproveNode';
export default SelfImproveNode;
